import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrlHeight } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import isChromatic from "chromatic/isChromatic";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";
var NEWS_REPORTS = [
    {
        mediaName: "fast-company",
        mediaLogo: "https://cdn.faire.com/fastly/8b49052255542b0b562d1087dcee07c077baa9d35aeb830a466ba8dded720f08.png",
        content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "o2rlR0",
            defaultMessage: [
                {
                    type: 0,
                    value: "\u201CThe 10 most innovative retail companies of 2020\u201D"
                }
            ]
        }),
        cta: "https://www.fastcompany.com/90457893/retail-most-innovative-companies-2020"
    },
    {
        mediaName: "wwd",
        mediaLogo: "https://cdn.faire.com/fastly/6eb7dbbad18a3f27d827dbb2bbfac8d3cbd15176aae3584df89768a408202638.png",
        content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "8NzUZ4",
            defaultMessage: [
                {
                    type: 0,
                    value: "\u201CFaire\u2019s first online trade show could show how an online version could work.\u201D"
                }
            ]
        }),
        cta: "https://wwd.com/business-news/retail/faire-summer-market-live-trade-shows-1203706701/"
    },
    {
        mediaName: "forbes",
        mediaLogo: "https://cdn.faire.com/fastly/26f3e4ccf3e5f2e5b979150dea590bf4476edd6ed34ddfba732e67eb00b4aa7f.png",
        content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "4Z4zHF",
            defaultMessage: [
                {
                    type: 0,
                    value: "\u201CA high-flying unicorn bet its business on local shops.\u201D"
                }
            ]
        }),
        cta: "https://www.forbes.com/sites/laurendebter/2020/03/26/faire-small-businesses-coronavirus/#4780379f17c2"
    }
];
var NewsModuleBase = function(_param) {
    var ref = _param.ref, props = _object_without_properties(_param, [
        "ref"
    ]);
    var strictLocalize = useStrictLocalization().strictLocalize;
    var className = props.className, eventPrefix = props.eventPrefix;
    useEffect(function() {
        // As the news reports only render when user scroll to this section
        // we want to preload the images to avoid flickering during the fadeIn animation
        var imagesToLoad = NEWS_REPORTS.map(function(newsReport) {
            return newsReport.mediaLogo;
        });
        imagesToLoad.forEach(function(url) {
            var window = getWindowOrThrow();
            var img = new window.Image();
            var _optimizeImageUrlHeight;
            img.src = (_optimizeImageUrlHeight = optimizeImageUrlHeight({
                url: url
            }, false, 27)) !== null && _optimizeImageUrlHeight !== void 0 ? _optimizeImageUrlHeight : "";
        });
    }, []);
    var isSSR = useIsSSR();
    var isMobile = useViewport().isMobile;
    return /*#__PURE__*/ _jsx(Wrapper, {
        className: className,
        ref: ref,
        children: /*#__PURE__*/ _jsxs(WhenVisibilityIsImminent, {
            threshold: 0,
            forceRender: isMobile || isChromatic(),
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: isMobile ? 80 : 0
                }),
                /*#__PURE__*/ _jsx(NewsReportsWrapper, {
                    children: NEWS_REPORTS.map(function(newsReport, index) {
                        return /*#__PURE__*/ _jsxs(NewsReportColumn, {
                            $index: index,
                            $isChromatic: isChromatic(),
                            children: [
                                /*#__PURE__*/ _jsx(Row, {
                                    align: "center",
                                    justify: "center",
                                    children: /*#__PURE__*/ _jsx(MediaLogo, {
                                        alt: strictLocalize({
                                            id: "ZEM3VZ",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "Media Logo"
                                                }
                                            ]
                                        }),
                                        src: optimizeImageUrlHeight({
                                            url: newsReport.mediaLogo
                                        }, isSSR, 27),
                                        height: 27
                                    })
                                }),
                                /*#__PURE__*/ _jsx(NewsReportContent, {
                                    children: newsReport.content
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "tertiary",
                                    target: "_blank",
                                    linkTo: newsReport.cta,
                                    onClick: trackEventOnClick("".concat(eventPrefix).concat(newsReport.mediaName)),
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "x1c6m3",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Read Article"
                                            }
                                        ]
                                    })
                                })
                            ]
                        }, newsReport.mediaLogo);
                    })
                })
            ]
        })
    });
};
export var NewsModule = observer(NewsModuleBase);
var Wrapper = styled(Column).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-812fcf39-0"
})([
    "max-width:1008px;height:404px;padding:0 72px;width:100%;",
    "{height:794px;padding:0;}"
], responsiveMediaQueries.mobile);
var NewsReportsWrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    },
    justify: {
        mobileAndAbove: "space-between",
        desktopAndAbove: "space-around"
    }
}).withConfig({
    componentId: "sc-812fcf39-1"
})([
    "width:100%;"
]);
var NewsReportColumn = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-812fcf39-2"
})([
    "",
    " ",
    "{animation:none;opacity:1;margin-bottom:80px;}"
], function(props) {
    return !props.$isChromatic ? css([
        "opacity:0;animation:fadeIn 0.5s ease 1 forwards;animation-delay:",
        "s;@keyframes fadeIn{to{opacity:1;}}"
    ], 0.5 * props.$index) : "";
}, responsiveMediaQueries.mobile);
var MediaLogo = styled.img.withConfig({
    componentId: "sc-812fcf39-3"
})([
    "margin-bottom:24px;"
]);
var NewsReportContent = styled(Typography).withConfig({
    componentId: "sc-812fcf39-4"
})([
    "&&&{margin-bottom:8px;text-align:center;max-width:272px;min-width:272px;",
    "{max-width:200px;min-width:200px;}}"
], responsiveMediaQueries.tabletAndBelow);
